import { useCallback, useEffect, useState } from 'react';
import type { CoreOrganization } from '@/lib/zifekCore';
import {
  createSchemaEntity,
  deleteSchemaEntity,
  fetchSchemaStats,
  listSchemaEntities,
  MODULE_GROUPS,
  type SchemaEntity,
} from '@/lib/coreSchemaMap';

interface Props {
  commerceId: number;
  org: CoreOrganization;
}

const INPUT = 'w-full px-3 py-2 rounded-md border border-background-200/70 bg-background-50 text-sm text-foreground-900 placeholder:text-foreground-400 focus:outline-none focus:ring-2 focus:ring-primary-300 focus:border-primary-300 transition-colors';

const MODULE_CLASS: Record<string, string> = {
  core: 'bg-primary-100 text-primary-900',
  finance: 'bg-accent-100 text-accent-900',
  hospitality: 'bg-secondary-100 text-secondary-900',
  commerce: 'bg-background-100 text-foreground-700',
};

export default function SchemaTab({ org }: Props) {
  const [entities, setEntities] = useState<SchemaEntity[]>([]);
  const [stats, setStats] = useState({ total: 0, core: 0, moduleTables: 0, modules: 0 });
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [busy, setBusy] = useState<number | null>(null);
  const [filter, setFilter] = useState('all');
  const [form, setForm] = useState({ entityKey: '', tableName: '', module: 'core', description: '' });
  const [saving, setSaving] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const [list, s] = await Promise.all([listSchemaEntities(), fetchSchemaStats()]);
      setEntities(list);
      setStats(s);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Chargement impossible.');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const onAdd = async () => {
    if (!form.entityKey.trim() || !form.tableName.trim()) { setError('Entité et table sont requises.'); return; }
    setSaving(true);
    setError('');
    try {
      await createSchemaEntity({
        entityKey: form.entityKey.trim(), tableName: form.tableName.trim(), module: form.module,
        isCore: form.module === 'core', description: form.description || null,
      });
      setForm({ entityKey: '', tableName: '', module: 'core', description: '' });
      await load();
    } catch (err) { setError(err instanceof Error ? err.message : 'Création impossible.'); } finally { setSaving(false); }
  };

  const remove = async (e: SchemaEntity) => {
    setBusy(e.id);
    try { await deleteSchemaEntity(e.id); await load(); } catch (err) { setError(err instanceof Error ? err.message : 'Erreur.'); } finally { setBusy(null); }
  };

  const filtered = filter === 'all' ? entities : entities.filter((e) => (e.module || 'core') === filter);

  return (
    <div className="space-y-5">
      <p className="text-xs text-foreground-500 max-w-3xl">
        <span className="font-semibold text-foreground-700">Architecture des tables</span> : le schéma suit une
        logique relationnelle claire. Les entités centrales vivent dans le socle ; chaque module (Finance,
        Hospitality, Commerce) possède ensuite ses propres tables.
      </p>

      {error && <div className="p-3 bg-red-50 border border-red-200 rounded-md text-xs text-red-700">{error}</div>}

      <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
        <div className="rounded-lg border bg-background-50 border-background-200/70 p-4">
          <i className="ri-layout-grid-line text-lg text-primary-600"></i>
          <p className="text-lg font-bold text-foreground-900 mt-1">{stats.total}</p>
          <p className="text-xs text-foreground-500">Entités / tables</p>
        </div>
        <div className="rounded-lg border bg-background-50 border-background-200/70 p-4">
          <i className="ri-stack-line text-lg text-primary-600"></i>
          <p className="text-lg font-bold text-foreground-900 mt-1">{stats.core}</p>
          <p className="text-xs text-foreground-500">Entités du socle</p>
        </div>
        <div className="rounded-lg border bg-background-50 border-background-200/70 p-4">
          <i className="ri-apps-2-line text-lg text-accent-600"></i>
          <p className="text-lg font-bold text-foreground-900 mt-1">{stats.moduleTables}</p>
          <p className="text-xs text-foreground-500">Tables de modules</p>
        </div>
        <div className="rounded-lg border bg-background-50 border-background-200/70 p-4">
          <i className="ri-node-tree text-lg text-secondary-600"></i>
          <p className="text-lg font-bold text-foreground-900 mt-1">{stats.modules}</p>
          <p className="text-xs text-foreground-500">Modules</p>
        </div>
      </div>

      <div className="bg-background-50 border border-background-200/70 rounded-lg p-4">
        <h4 className="text-sm font-semibold text-foreground-900 mb-2 flex items-center gap-2"><i className="ri-apps-2-line text-primary-600"></i>Tables par module</h4>
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-2">
          {MODULE_GROUPS.map((g) => (
            <div key={g.key} className="rounded-lg border border-background-200/70 bg-background-100/60 p-3">
              <p className="text-xs font-semibold text-foreground-800 flex items-center gap-2"><i className={`${g.icon} text-primary-600`}></i>{g.label}</p>
              <ul className="mt-1.5 space-y-1">
                {g.tables.map((t) => (
                  <li key={t.table} className="text-[11px] text-foreground-500 flex items-center gap-1.5"><i className="ri-table-line text-foreground-400"></i>{t.label}</li>
                ))}
              </ul>
            </div>
          ))}
        </div>
      </div>

      <div className="bg-background-50 border border-background-200/70 rounded-lg overflow-hidden">
        <div className="px-4 py-3 border-b border-background-200/60 flex items-center justify-between gap-3 flex-wrap">
          <div className="flex items-center gap-2">
            <span className="w-8 h-8 rounded-lg bg-primary-100 text-primary-700 flex items-center justify-center"><i className="ri-layout-grid-line"></i></span>
            <h4 className="text-sm font-semibold text-foreground-900">Entités centrales & relations</h4>
          </div>
          <select className="px-3 py-1.5 rounded-md border border-background-200/70 bg-background-50 text-xs text-foreground-900 focus:outline-none focus:ring-2 focus:ring-primary-300" value={filter} onChange={(e) => setFilter(e.target.value)}>
            <option value="all">Tous les modules</option>
            {MODULE_GROUPS.map((g) => <option key={g.key} value={g.key}>{g.label}</option>)}
          </select>
        </div>
        {loading ? (
          <div className="flex items-center justify-center py-12"><i className="ri-loader-4-line animate-spin text-2xl text-primary-500"></i></div>
        ) : filtered.length === 0 ? (
          <p className="px-4 py-6 text-xs text-foreground-400 text-center">Aucune entité.</p>
        ) : (
          <div className="divide-y divide-background-200/60 max-h-[32rem] overflow-y-auto">
            {filtered.map((e) => (
              <div key={e.id} className="px-4 py-3">
                <div className="flex items-center gap-3 flex-wrap">
                  <span className="w-9 h-9 rounded-lg bg-background-100 text-foreground-700 flex items-center justify-center flex-shrink-0"><i className="ri-table-line"></i></span>
                  <div className="min-w-0 flex-1">
                    <p className="text-sm font-medium text-foreground-800 flex items-center gap-2">
                      {e.entity_key}
                      <span className={`text-[10px] px-2 py-0.5 rounded-full ${MODULE_CLASS[e.module || 'core'] || 'bg-background-100 text-foreground-600'}`}>{e.module}</span>
                      {e.is_core && <span className="text-[10px] px-2 py-0.5 rounded-full bg-primary-100 text-primary-900">socle</span>}
                    </p>
                    <p className="text-[11px] text-foreground-400 truncate"><code className="bg-background-100 px-1 rounded">{e.table_name}</code> · {e.description}</p>
                  </div>
                  <button onClick={() => remove(e)} disabled={busy === e.id} className="px-2.5 py-1 rounded-full bg-red-100 text-red-800 text-[11px] font-medium whitespace-nowrap cursor-pointer disabled:opacity-40">Retirer</button>
                </div>
                <div className="mt-2 pl-12 flex flex-wrap gap-1.5">
                  {(e.columns || []).slice(0, 8).map((c) => (
                    <span key={c} className="text-[10px] px-2 py-0.5 rounded-full bg-background-100 text-foreground-600">{c}</span>
                  ))}
                  {(e.relations || []).length > 0 && (
                    <span className="text-[10px] px-2 py-0.5 rounded-full bg-secondary-100 text-secondary-900 flex items-center gap-1"><i className="ri-links-line"></i>{(e.relations || []).length} relations</span>
                  )}
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      <div className="bg-background-50 border border-background-200/70 rounded-lg p-4">
        <h4 className="text-sm font-semibold text-foreground-900 mb-2 flex items-center gap-2"><i className="ri-add-line text-accent-600"></i>Déclarer une entité / table</h4>
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
          <input className={INPUT} placeholder="Entité (ex : subscriptions)" value={form.entityKey} onChange={(e) => setForm({ ...form, entityKey: e.target.value })} />
          <input className={INPUT} placeholder="Table (ex : core_subscriptions)" value={form.tableName} onChange={(e) => setForm({ ...form, tableName: e.target.value })} />
          <select className={INPUT} value={form.module} onChange={(e) => setForm({ ...form, module: e.target.value })}>
            {MODULE_GROUPS.map((g) => <option key={g.key} value={g.key}>{g.label}</option>)}
          </select>
        </div>
        <input className={`${INPUT} mt-3`} placeholder="Description" value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} />
        <button onClick={onAdd} disabled={saving} className="mt-3 px-5 py-2 rounded-full bg-primary-500 text-background-50 text-sm font-semibold whitespace-nowrap hover:bg-primary-600 transition-colors cursor-pointer disabled:opacity-50 flex items-center gap-2">
          {saving ? <i className="ri-loader-4-line animate-spin"></i> : <i className="ri-add-line"></i>} Déclarer
        </button>
      </div>
    </div>
  );
}